import { useEffect } from 'react';

export default function StructuredData({
  type = 'WebSite',
  title,
  description,
  url,
  image,
  author = 'Ryan Deeds',
  datePublished,
  dateModified,
  experimentData = null
}) {
  const baseUrl = 'https://deedslab.com';

  useEffect(() => {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create structured data object
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': type,
      name: title,
      description: description,
      url: url || baseUrl,
      image: image || `${baseUrl}/social-share-landing.png`,
      author: {
        '@type': 'Person',
        name: author
      },
      publisher: {
        '@type': 'Organization',
        name: 'deedslab.com',
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl}/dx.svg`
        }
      }
    };

    // Add experiment-specific data if available
    if (experimentData && type === 'Article') {
      structuredData.headline = experimentData.name;
      structuredData.articleSection = experimentData.status?.state || 'Experiment';
      structuredData.keywords = ['experiment', 'innovation', 'strategy', 'testing', experimentData.slug];
      if (datePublished) structuredData.datePublished = datePublished;
      if (dateModified) structuredData.dateModified = dateModified;

      // Add breadcrumb navigation
      structuredData.breadcrumb = {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: baseUrl
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Experiments',
            item: `${baseUrl}/#experiments`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: experimentData.name,
            item: `${baseUrl}/experiments/${experimentData.slug}`
          }
        ]
      };
    }

    // Add website-specific data for homepage
    if (type === 'WebSite') {
      structuredData.potentialAction = {
        '@type': 'SearchAction',
        target: `${baseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string'
      };

      structuredData.mainEntity = {
        '@type': 'ItemList',
        name: 'Experiments',
        description: 'A collection of strategic experiments and interactive simulations'
      };
    }

    // Inject the script tag
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData, null, 2);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, title, description, url, image, author, datePublished, dateModified, experimentData]);

  return null; // This component doesn't render anything
}
